import { apiClient } from './client';
import type {
  DepartmentTeam,
  OnsiteLiveTeam,
  OnsiteLiveTeamWrite,
  TeamLimit,
  TeamLimits,
  TeamLimitWrite,
  TeamRuleApplyResult,
  TeamRulePreview,
} from '@/types/oscar';

/**
 * Typed wrappers over the Teams and limits endpoints (OnsiteLiveTeamFunctions.cs;
 * plan D10, D13, D14). Reads are admin + helpdesk; every write is admin-only.
 * Team, limit and apply writes are refused (409) until the OnsiteLive cutover.
 */

const base = '/api/onsitelive-admin';

export function listOnsiteLiveTeams(signal?: AbortSignal): Promise<OnsiteLiveTeam[]> {
  return apiClient.get<OnsiteLiveTeam[]>(`${base}/teams`, signal);
}

export function saveOnsiteLiveTeam(id: number | null, body: OnsiteLiveTeamWrite): Promise<OnsiteLiveTeam> {
  return id === null
    ? apiClient.post<OnsiteLiveTeam>(`${base}/teams`, body)
    : apiClient.patch<OnsiteLiveTeam>(`${base}/teams/${id}`, body);
}

export function listDepartmentTeams(signal?: AbortSignal): Promise<DepartmentTeam[]> {
  return apiClient.get<DepartmentTeam[]>(`${base}/department-teams`, signal);
}

export function setDepartmentTeam(code: string, teamId: number): Promise<DepartmentTeam> {
  return apiClient.put<DepartmentTeam>(`${base}/department-teams/${encodeURIComponent(code)}`, { TeamId: teamId });
}

/** The department falls back to Default (CatchAll): no approval authority. */
export function removeDepartmentTeam(code: string): Promise<DepartmentTeam> {
  return apiClient.post<DepartmentTeam>(`${base}/department-teams/${encodeURIComponent(code)}/remove`, {});
}

export function listTeamLimits(signal?: AbortSignal): Promise<TeamLimits> {
  return apiClient.get<TeamLimits>(`${base}/limits`, signal);
}

export function createTeamLimit(body: TeamLimitWrite): Promise<TeamLimit> {
  return apiClient.post<TeamLimit>(`${base}/limits`, body);
}

export function updateTeamLimitAmount(id: number, amount: number): Promise<TeamLimit> {
  return apiClient.patch<TeamLimit>(`${base}/limits/${id}`, { Amount: amount });
}

export function removeTeamLimit(id: number): Promise<{ TeamLimitId: number; IsDeleted: boolean }> {
  return apiClient.post<{ TeamLimitId: number; IsDeleted: boolean }>(`${base}/limits/${id}/remove`, {});
}

export function getTeamRulePreview(signal?: AbortSignal): Promise<TeamRulePreview> {
  return apiClient.get<TeamRulePreview>(`${base}/team-rule`, signal);
}

/** Stores the rule's team for these users only - the ones the reviewer saw - and only if they still differ. */
export function applyTeamRule(onsiteLiveUserIds: number[]): Promise<TeamRuleApplyResult> {
  return apiClient.post<TeamRuleApplyResult>(`${base}/team-rule/apply`, { OnsiteLiveUserIds: onsiteLiveUserIds });
}
